/**
 * The local player's thrown rocks, predicted (2026-10-27 feel pass). The
 * rock grows in front of the wizard while Force is held (ClientState keeps
 * the hold), and the moment the release is sent it flies along exactly the
 * path the server will give it (shared rockThrow.ts: the same position, aim
 * and hold), so it never pops in or jumps. The server's copy (its "twin",
 * matched in launch order) is hidden while ours flies.
 *
 * Ours takes its flight a touch slower, stretched by the expected network
 * delay, so it comes down as the server's burst arrives. If the twin bursts
 * early (it hit a body on the way) ours ends where it is, which by then is
 * where the twin burst. A rock whose twin never shows (the server refused the
 * throw) fades out after it lands.
 */

import type { Arena, Element, ProjSnap, Vec2 } from '@shared/index.js';
import { fromAngle, rockFlight, rockHeight, rockMuzzle, rockReach } from '@shared/index.js';
import type { ViewProjectile } from './viewModel.js';

interface PredictedRock {
  /** Negative local id until the twin is known. */
  key: number;
  el: Element[];
  start: Vec2;
  aim: number;
  speed: number;
  /** The throw's distance (its arc), and where the map stops it first (pillar, wall, edge). */
  range: number;
  stop: number;
  radius: number;
  /** Local ms of the launch, and how long our flight takes (the real flight plus the expected delay). */
  at: number;
  duration: number;
  twin: number | null;
  landedAt: number | null;
  gone: boolean;
}

/** A landed rock waits this long for its twin's burst before it goes anyway (ms). */
const LAND_HOLD_MS = 450;
/** A rock whose twin never showed goes this long after landing: the server refused the throw (ms). */
const NO_TWIN_MS = 700;
/**
 * A twin stays hidden this long after the server drops it: the drawn view runs an interpolation delay
 * (INTERP_DELAY_MS, 150 ms) behind the snapshots, so it would otherwise fly its last stretch as a second rock (ms).
 */
export const TWIN_LINGER_MS = 300;

export class RockPredictor {
  private rocks: PredictedRock[] = [];
  private nextKey = -1;
  /** Ids of our server boulders seen so far, so each new one becomes a twin once. */
  private readonly seen = new Set<number>();
  /** Twins to keep hidden, and the local ms each stops being hidden (Infinity while the server still flies it). */
  private readonly twins = new Map<number, number>();

  /**
   * Throw a rock now: `pos` and `aim` as the server will have them, `fullRange` the spell's, `latencyMs` the expected
   * delay. On `arena` it stops where a pillar, wall or the edge stops the server's.
   */
  launch(now: number, pos: Vec2, aim: number, casterRadius: number, fullRange: number, charge: number, el: readonly Element[], latencyMs: number, arena: Arena | null): void {
    const f = rockFlight(fullRange, charge);
    const start = rockMuzzle(pos, aim, casterRadius, f.radius);
    const stop = arena ? rockReach(arena, start, aim, f.range, f.radius) : f.range;
    this.rocks.push({
      key: this.nextKey--,
      el: [...el],
      start,
      aim,
      speed: f.speed,
      range: f.range,
      stop,
      radius: f.radius,
      at: now,
      duration: (stop / f.speed) * 1000 + Math.max(0, latencyMs),
      twin: null,
      landedAt: null,
      gone: false,
    });
  }

  /** The newest snapshot: new boulders of ours become twins (oldest rock first); a twin gone means it burst. */
  onSnap(projectiles: readonly ProjSnap[], pid: number, now: number): void {
    const mine = projectiles.filter((p) => p.o === pid && p.k === 'boulder').sort((a, b) => a.id - b.id);
    const live = new Set(mine.map((p) => p.id));
    for (const p of mine) {
      if (this.seen.has(p.id)) continue;
      this.seen.add(p.id);
      const r = this.rocks.find((x) => x.twin === null && !x.gone);
      if (r) {
        r.twin = p.id;
        this.twins.set(p.id, Infinity);
      }
    }
    for (const r of this.rocks) if (r.twin !== null && !live.has(r.twin)) r.gone = true;
    for (const id of this.seen) if (!live.has(id)) this.seen.delete(id);
    for (const [id, until] of this.twins) if (until === Infinity && !live.has(id)) this.twins.set(id, now + TWIN_LINGER_MS);
  }

  /** Server boulders to hide at local time `now`: our rocks' twins, while the server flies them and until the drawn view has caught up. */
  hidden(now: number): Set<number> {
    const out = new Set<number>();
    for (const [id, until] of this.twins) {
      if (now >= until) this.twins.delete(id);
      else out.add(id);
    }
    return out;
  }

  /** Our rocks at local time `now`, drawn like any projectile (with `z`). */
  views(now: number, pid: number): ViewProjectile[] {
    const out: ViewProjectile[] = [];
    for (const r of this.rocks) {
      const t = Math.min(1, Math.max(0, (now - r.at) / r.duration));
      if (t >= 1) {
        r.landedAt ??= now;
        const wait = r.twin === null ? NO_TWIN_MS : LAND_HOLD_MS;
        if (now - r.landedAt > wait) r.gone = true;
      }
      if (r.gone) continue;
      const traveled = t * r.stop;
      const d = fromAngle(r.aim, traveled);
      const v = fromAngle(r.aim, t >= 1 ? 0 : r.speed);
      const x = r.start.x + d.x, y = r.start.y + d.y;
      out.push({
        id: r.twin ?? r.key, o: pid, k: 'boulder', x, y, vx: v.x, vy: v.y, el: r.el, r: r.radius,
        z: rockHeight(traveled, r.range), rx: x, ry: y, facing: r.aim, isLocal: true,
      });
    }
    this.rocks = this.rocks.filter((r) => !r.gone);
    return out;
  }

  clear(): void {
    this.rocks = [];
    this.seen.clear();
    this.twins.clear();
  }
}
